import { apiClient } from '@/core/api';

import type { ImportJob } from './types';

export interface ImportShapeOptions {
  /** Drop the Confluence space home page and promote its children to the top. */
  skipHomePage?: boolean;
  /** Nest everything under one new page with this title (optional). */
  parentTitle?: string;
}

function shapeFields(form: FormData, opts: ImportShapeOptions = {}) {
  form.append('skip_home_page', opts.skipHomePage ? 'true' : 'false');
  if (opts.parentTitle) form.append('parent_title', opts.parentTitle);
}

/** Start a Confluence-HTML import into a new wiki collection. */
export async function startWikiImport(
  file: File,
  opts: ImportShapeOptions & { collectionName?: string } = {},
): Promise<ImportJob> {
  const form = new FormData();
  form.append('file', file);
  if (opts.collectionName) form.append('collection_name', opts.collectionName);
  shapeFields(form, opts);
  // axios sets the multipart boundary from the FormData.
  return (await apiClient.post<ImportJob>('/wiki/imports/confluence/', form)).data;
}

/** Start a Confluence-HTML import into an existing project's Pages.
 *  Note the doubled segment: the projects app is mounted at `/api/projects/` and
 *  its router registers `projects`, so a project lives at `/projects/projects/<id>/`. */
export async function startProjectImport(
  projectPublicId: string,
  file: File,
  opts: ImportShapeOptions = {},
): Promise<ImportJob> {
  const form = new FormData();
  form.append('file', file);
  shapeFields(form, opts);
  return (
    await apiClient.post<ImportJob>(
      `/projects/projects/${projectPublicId}/imports/confluence/`,
      form,
    )
  ).data;
}

export async function getImportJob(publicId: string): Promise<ImportJob> {
  return (await apiClient.get<ImportJob>(`/imports/jobs/${publicId}/`)).data;
}
